import { CheckCircle2, Loader2, PauseCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useToast } from '@/hooks/useToast'
import { useImportOlder } from '@/features/inbox/queries'
import type { InboxAccount } from '@/features/inbox/types'

const RECENT_DONE_WINDOW_MS = 10 * 60 * 1000

function describeQuery(query: string | null): string {
  if (!query) return ''
  let source = 'All mail'
  if (/\bin:sent\b/.test(query)) source = 'Inbox + Sent'
  else if (/\bin:inbox\b/.test(query)) source = 'Inbox'
  let window = 'all time'
  const days = query.match(/newer_than:(\d+)d/)
  if (days) {
    const n = Number(days[1])
    window = n >= 365 ? 'last 12 months' : `last ${n} days`
  } else if (/\b(after|before):/.test(query)) {
    window = 'a custom date range'
  }
  return `${source} · ${window}`
}

export function BackfillProgress({
  account,
  canImportOlder,
}: {
  account: InboxAccount
  canImportOlder: boolean
}) {
  const { toast } = useToast()
  const importOlder = useImportOlder()
  const status = account.backfill_status
  const finishedAt = account.backfill_finished_at
    ? new Date(account.backfill_finished_at).getTime()
    : 0
  const recentlyDone =
    status === 'done' && finishedAt > 0 && Date.now() - finishedAt < RECENT_DONE_WINDOW_MS
  if (status !== 'running' && status !== 'paused' && !recentlyDone) return null

  const imported = account.backfill_imported ?? 0
  const label = describeQuery(account.backfill_query)
  const pausedForCap = status === 'paused' && account.backfill_paused_reason === 'cap'
  const pausedForQuota = status === 'paused' && account.backfill_paused_reason === 'quota'

  const onImportOlder = async () => {
    try {
      await importOlder.mutateAsync(account.id)
      toast({ title: 'Importing older mail…' })
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not resume import'
      toast({ title: 'Import failed', description: msg, variant: 'destructive' })
    }
  }

  return (
    <div className="mt-2 rounded-md border border-border bg-muted/40 p-3">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        {status === 'running' ? (
          <Loader2 className="size-4 animate-spin text-primary" />
        ) : recentlyDone ? (
          <CheckCircle2 className="size-4 text-primary" />
        ) : (
          <PauseCircle className="size-4 text-muted-foreground" />
        )}
        <span className="font-medium">
          {status === 'running'
            ? 'Importing your mailbox…'
            : recentlyDone
              ? 'Import complete'
              : 'Import paused'}
        </span>
        <span className="text-muted-foreground">
          {imported.toLocaleString()} email{imported === 1 ? '' : 's'}
          {recentlyDone ? ' imported' : ' so far'}
        </span>
      </div>
      {label ? <p className="mt-1 text-xs text-muted-foreground">Importing: {label}</p> : null}
      <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-border">
        <div
          className={
            status === 'running'
              ? 'h-full w-full animate-pulse rounded-full bg-primary'
              : 'h-full w-full rounded-full bg-primary'
          }
        />
      </div>
      {pausedForQuota ? (
        <p className="mt-2 text-xs text-muted-foreground">
          The AI classification quota was reached. The import resumes automatically when your
          quota refreshes.
        </p>
      ) : null}
      {pausedForCap ? (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <p className="text-xs text-muted-foreground">
            Reached the first-import limit. Older mail is still in your mailbox.
          </p>
          {canImportOlder ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={onImportOlder}
              disabled={importOlder.isPending}
            >
              {importOlder.isPending ? 'Starting…' : 'Import older'}
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
